/**
 * 国際ドラフツ (Draughts 100) の TypeScript 表現 (mixed-engine の serde 形と一致)。
 *
 * - 盤は 10x10、 黒マス 50 のみ使用 (PDN 番号 1..=50 を index 0..=49 に map)
 * - 強制取り + 最大取り (FMJD majority capture) → legal() は最大 capture 経路のみ返す
 * - Capture.path は中間着地点を全て明示する full-form (例 PDN 28→17→6 なら path=[16, 5])
 */

export type Side = 'First' | 'Second';

export type DraughtsPiece =
  | 'Empty'
  | 'WhiteMan'
  | 'BlackMan'
  | 'WhiteKing'
  | 'BlackKing';

export type DraughtsEndKind =
  | 'NoMoves'
  | 'Resign'
  | 'Timeout'
  | 'Repetition'
  | 'NoProgress25'
  | 'EndgameLimit'
  | 'AgreedDraw';

export interface DraughtsOutcome {
  winner: Side | null;
  kind: DraughtsEndKind;
}

export type EndgameLimit = 'FiveMove' | 'SixteenMove';

export interface DraughtsState {
  squares: DraughtsPiece[]; // length 50
  to_move: Side;
  move_number: number;
  plies_since_progress: number;
  endgame_plies: number;
  endgame_mode: EndgameLimit | null;
  position_counts: Record<string, number>;
  current_hash: string;
  outcome: DraughtsOutcome | null;
}

export type DraughtsMove =
  | { Step: { from: number; to: number } }
  | { Capture: { from: number; path: number[] } };

export const DRAUGHTS_SIZE = 10;
export const DRAUGHTS_NUM_DARK = 50;

export function isStep(m: DraughtsMove): m is { Step: { from: number; to: number } } {
  return typeof m === 'object' && 'Step' in m;
}

export function isCapture(m: DraughtsMove): m is { Capture: { from: number; path: number[] } } {
  return typeof m === 'object' && 'Capture' in m;
}

/** Move の起点 (idx 0..=49)。 */
export function moveFrom(m: DraughtsMove): number {
  return isStep(m) ? m.Step.from : m.Capture.from;
}

/** Move の最終着地 (idx 0..=49)。 */
export function moveTo(m: DraughtsMove): number {
  if (isStep(m)) return m.Step.to;
  return m.Capture.path[m.Capture.path.length - 1];
}

/** 取りで通過する land 点列 (= path)。 Step では空配列。 */
export function moveLandings(m: DraughtsMove): number[] {
  if (isCapture(m)) return m.Capture.path;
  return [];
}

/** PDN 番号 (1..=50) → (row 0..=9, col 0..=9)。 row 0 = 黒陣 (上)、 row 9 = 白陣 (下)。 */
export function pdnToRc(pdn: number): [number, number] {
  const n = pdn - 1;
  const row = Math.floor(n / 5);
  const colInRow = n % 5;
  const col = row % 2 === 0 ? colInRow * 2 + 1 : colInRow * 2;
  return [row, col];
}

/** idx (0..=49) → (row, col)。 */
export function idxToRc(idx: number): [number, number] {
  return pdnToRc(idx + 1);
}

/** idx (0..=49) → PDN (1..=50)。 */
export function idxToPdn(idx: number): number {
  return idx + 1;
}

/** (row, col) → idx 0..=49、 黒マス以外は null。 */
export function rcToIdx(row: number, col: number): number | null {
  if (row < 0 || row >= DRAUGHTS_SIZE || col < 0 || col >= DRAUGHTS_SIZE) return null;
  const isDark = row % 2 === 0 ? col % 2 === 1 : col % 2 === 0;
  if (!isDark) return null;
  const colInRow = row % 2 === 0 ? (col - 1) / 2 : col / 2;
  return row * 5 + colInRow;
}

/** legal の中から from が一致する全 capture/step を抽出 (UI で from クリック → to 候補ハイライト用)。 */
export function legalMovesFrom(legal: DraughtsMove[], fromIdx: number): DraughtsMove[] {
  return legal.filter((m) => moveFrom(m) === fromIdx);
}

/** 取りで取られる相手駒の idx 列を計算 (deferred removal なので path から逆算)。
 *  state の駒配置を見ながら from→path 順に直線上の最初の enemy 駒を拾う。 */
export function capturedIdxsFor(state: DraughtsState, mv: DraughtsMove): number[] {
  if (!isCapture(mv)) return [];
  const sq = state.squares.slice();
  const me = state.to_move;
  let prev = mv.Capture.from;
  const captured: number[] = [];
  const enemyOwner: Side = me === 'First' ? 'Second' : 'First';
  for (const land of mv.Capture.path) {
    const [pr, pc] = idxToRc(prev);
    const [lr, lc] = idxToRc(land);
    const dr = Math.sign(lr - pr);
    const dc = Math.sign(lc - pc);
    let n = 1;
    while (true) {
      const r = pr + dr * n;
      const c = pc + dc * n;
      if (r === lr && c === lc) break;
      const idx = rcToIdx(r, c);
      if (idx == null) break;
      const cell = sq[idx];
      if (cell === 'Empty') {
        n++;
        continue;
      }
      const owner = cell === 'WhiteMan' || cell === 'WhiteKing' ? 'First' : 'Second';
      if (owner === enemyOwner && !captured.includes(idx)) {
        captured.push(idx);
        break;
      }
      n++;
    }
    prev = land;
  }
  return captured;
}
